import type { CalendarDate, IsoTimestamp } from '~/types/dates'
import { toCalendarDate } from '~/types/dates'
import {
  type MeasurementSessionId,
  newId,
  type OpinionId,
  type PhaseId,
  type ProjectId,
  type WeeklyReviewId,
} from '~/types/ids'
import type { Result } from '~/utils/result'
import { err, ok } from '~/utils/result'
import type { AdherenceCheck } from './adherenceCheck'
import { issue, type RecordEnvelope, type ValidationIssues } from './common'
import { type ContextTag, isContextTag } from './contextTags'
import type { DomainIntention, DomainPriority, FitnessDomain, PlannedItem } from './fitnessDomain'
import type { NutritionSource } from './nutritionPlan'
import { isPhaseType, type PhaseType } from './phase'
import type { RecoveryCheck } from './recoveryCheck'
import { DB_SCHEMA_VERSION } from './schemaVersion'
import type { WeeklySchedule } from './trainingSchedule'

/**
 * One week's review (data-model.md).
 *
 * A review is a draft until it is finalized, and finalizing is the moment the
 * week stops being editable. Everything the rules engine read is frozen onto the
 * record at that point, so the opinion the user was given stays explainable
 * afterwards — see `phaseSnapshot` below.
 */
export type WeeklyReviewStatus = 'draft' | 'complete'

/**
 * The wizard's steps, as stable vocabulary keys (the D-040 pattern).
 *
 * Stored on the draft so a reload resumes where the user left off rather than
 * at the beginning — the defect seen on a physical device at Checkpoint 1.8,
 * where reloading the Review screen discarded unsaved readings.
 *
 * Keys rather than indexes, deliberately. A stored `3` would silently point at
 * a different step the moment one is inserted, and inserting one is exactly what
 * Checkpoints 2.2 and 3 do when sentinel lifts and photos arrive.
 *
 * `strength` was inserted at Checkpoint 2.2, between `measurements` and
 * `recovery`. **A draft written by 2.1 resumes correctly across that change**,
 * which is the property the key-not-index choice was made for: a stored
 * `'recovery'` still means the recovery step, where a stored `3` would now point
 * at strength instead.
 *
 * Photos are still absent because photo capture does not exist. A step that
 * named an unbuilt feature would be a mock, and the wizard says where it lands
 * instead (D-056).
 */
export const WEEKLY_REVIEW_STEPS = [
  'trend',
  'measurements',
  'strength',
  'recovery',
  'nutrition',
  'adherence',
  'notes',
  'decision',
] as const

export type WeeklyReviewStep = (typeof WEEKLY_REVIEW_STEPS)[number]

export function isWeeklyReviewStep(value: string): value is WeeklyReviewStep {
  return (WEEKLY_REVIEW_STEPS as readonly string[]).includes(value)
}

/**
 * An immutable value copy of the phase configuration at finalization (D-046).
 *
 * This is **not** a duplicate of `phaseId`, and the two are not interchangeable.
 * `phaseId` is a foreign key: it groups reviews under a phase and draws phase
 * boundaries, and it follows the phase wherever it goes. `phaseSnapshot` is a
 * copy of what the phase *said* when the opinion was produced.
 *
 * The distinction is what makes editing phase history safe. D-070 lets the user
 * correct a phase's start date, end date, notes and transition reason. Without a
 * snapshot, moving a start date would silently change which week a finalized
 * review belonged to, and changing a target range would retroactively rewrite
 * whether a past week was on track — the app would appear to have said something
 * it never said. With one, a correction moves what is computed live from phase
 * dates, and leaves every conclusion already given exactly as it was given.
 *
 * `weekNumber` is copied for the same reason: it is derived from the phase start
 * date, so it is precisely the value a later correction would move.
 */
export interface PhaseSnapshot {
  readonly phaseType: PhaseType
  readonly startDate: CalendarDate
  readonly plannedEndDate: CalendarDate
  readonly targetWeeklyChangeMinPercent: number
  readonly targetWeeklyChangeMaxPercent: number
  /** Inclusive, anchored to the phase start date (D-034). */
  readonly weekNumber: number
}

/**
 * An immutable value copy of the **plan** the week was judged against (D-099,
 * as amended at C6.5).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  THIS EXISTS BECAUSE THE CHANGE HISTORY CANNOT ANSWER THE QUESTION
 * ────────────────────────────────────────────────────────────────────────────
 *
 * D-099 originally said that a commitment edit appending to the domain goal's
 * change history would mean "a finished week still reports what it was actually
 * planned to contain". **It does not, and cannot.** `DomainGoalChange` records
 * that a commitment moved and when; it does not record what the commitment
 * *was*. From an entry reading *the commitment changed on the 20th* there is no
 * way to rebuild the plan in force during the week of the 10th.
 *
 * So this is `phaseSnapshot`'s sibling, for the same reason and by the same
 * mechanism (D-046). `phaseSnapshot` stops a phase correction retroactively
 * changing whether a past week was on track; this stops a plan edit
 * retroactively changing what a past week was supposed to contain.
 *
 * **Written once, at finalization, from the plan applicable to that week.** It
 * is never recomputed, never rebuilt from the current plan, and never edited —
 * a finalized review and its snapshot are immutable. Historical UI and every
 * export read this, and **must never join an old review to the live plan**;
 * that join is precisely the retroactive re-judgement D-046 forbids.
 *
 * **Absent on reviews written before C6.5, and never backfilled.** Inventing one
 * from today's plan would assert knowledge the app does not have — the same
 * reasoning that leaves a backfilled project `active` with no outcome (D-080),
 * and that makes an unanswered day missing evidence rather than a failure
 * (D-096). Readers render an explicit *not recorded* state.
 */
export interface PlanSnapshotGoal {
  readonly domain: FitnessDomain
  readonly intention: DomainIntention
  readonly priority: DomainPriority
  /**
   * The two variants that describe a week. `short` is deliberately omitted —
   * it is a display convenience between the two that matter, and a snapshot is
   * for answering "what was asked of me, and what was the floor?".
   */
  readonly standard?: readonly PlannedItem[]
  readonly minimumViable?: readonly PlannedItem[]
  /**
   * The recurring week as it stood when this review was finalized (D-105 §12).
   *
   * **Frozen, like everything else here.** `standard` is derived from this
   * schedule for a goal that has one, so storing both is not two authorities —
   * it is the derived value and the thing it was derived from, kept together
   * because a reader of an old export cannot run `standardWeekFor` and a reader
   * of the schedule alone cannot tell what the app told the user that week.
   *
   * Absent for a goal with no schedule, which is every goal before C7.5 and any
   * goal still planned as a quota.
   */
  readonly schedule?: WeeklySchedule
}

export interface PlanSnapshotNutrition {
  readonly source: NutritionSource
  /** Free text the user typed, so it gates on `includeNotes` in every export. */
  readonly providerLabel?: string
  readonly calorieTarget?: number
  readonly calorieTargetBasis?: string
  readonly proteinTargetG?: number
}

export interface PlanSnapshot {
  readonly activeDomains: readonly FitnessDomain[]
  readonly goals: readonly PlanSnapshotGoal[]
  readonly nutrition?: PlanSnapshotNutrition
  /**
   * The routine times as they stood that week.
   *
   * **Stored here and omitted from every export, unconditionally.** D-099 §B is
   * explicit that an anchor never reaches an AI-review file — it says nothing
   * about the body and everything about when the user is tired of being asked.
   * It is kept in the record so historical UI can show it; the gate lives in
   * `serializeWeeklyReport` and nowhere else, so the PDF and CSV inherit the
   * omission rather than each having to remember it (D-085).
   */
  readonly anchors: readonly { readonly kind: string; readonly startTime?: string }[]
  /** What is needed to interpret everything above. */
  readonly rulesVersion: string
  readonly schemaVersion: number
}

export interface WeeklyReviewRecord extends RecordEnvelope {
  readonly id: WeeklyReviewId
  /** Indexed. The configured week start, default Monday (D-034). */
  readonly weekStartDate: CalendarDate
  /** Indexed. */
  readonly status: WeeklyReviewStatus
  /** Indexed. Absent only where a review was somehow begun with no active phase. */
  readonly phaseId?: PhaseId
  /**
   * Indexed. The project the week belongs to (D-062).
   *
   * A second foreign key rather than a lookup through `phaseId`, because a
   * project spans several phases and grouping a project's weeks must not
   * require walking phase history to find out which project each phase was in.
   */
  readonly projectId?: ProjectId
  /** Written once, at finalization. Never present on a draft. */
  readonly phaseSnapshot?: PhaseSnapshot
  /**
   * Written once, at finalization. Never present on a draft, and **absent on
   * every review finalized before C6.5** — where it is never backfilled.
   */
  readonly planSnapshot?: PlanSnapshot
  readonly recovery?: RecoveryCheck
  readonly adherence?: AdherenceCheck
  readonly measurementSessionId?: MeasurementSessionId
  readonly opinionId?: OpinionId
  readonly notes?: string
  /** Possible explanations for the week, never proven causes (D-040). */
  readonly contextTags: readonly ContextTag[]
  /** Where the wizard was when it last saved. Absent on a review never resumed. */
  readonly draftStep?: WeeklyReviewStep
  readonly finalizedAt?: IsoTimestamp
}

/**
 * Where to reopen the wizard.
 *
 * An unrecognised stored step falls back to the first rather than failing. That
 * is not defensive padding: a draft written by a build whose step list differed
 * is a real case the moment 2.2 or 3 inserts a step, and stranding the user on a
 * step that no longer exists would be worse than starting them at the top with
 * their answers intact.
 */
export function resumeStepFor(record: WeeklyReviewRecord): WeeklyReviewStep {
  const stored = record.draftStep
  return stored !== undefined && isWeeklyReviewStep(stored) ? stored : WEEKLY_REVIEW_STEPS[0]
}

/**
 * The steps this user's review actually has (D-090).
 *
 * **Computed from the active domains**, the same rule the setup wizard follows.
 * A mobility-only user is not asked to confirm a weight trend they do not record,
 * and is not shown a measurement form for a site nothing reads.
 *
 * Three of the seven are unconditional and each for its own reason:
 *
 *   - **`recovery`** is a cross-cutting guardrail, not a domain. It applies
 *     whatever the user is working on, which is exactly why it is not in any
 *     domain's evidence list — attaching it to one would let it be switched off
 *     by pausing that goal.
 *   - **`adherence`** carries the planned-and-completed session counts, which are
 *     meaningful for every active domain.
 *   - **`notes`** and **`decision`** close every review.
 *
 * **`strength` is shown when body composition is active, even if the strength
 * domain is not.** Those are two different things: the strength *guardrail*
 * inside the body-composition opinion reads sentinel lifts to catch a decline
 * during a deficit, and it needs the sets whether or not the user has a strength
 * goal. The strength *domain* is a separate, unimplemented thing.
 *
 * **No cardio or mobility step is added here.** Neither has an approved evidence
 * design yet — cardio's arrives with the wearable import, mobility's has no
 * approved basis at all — and a step collecting a field nothing reads is the
 * converse of D-060. A user with those domains has them recorded elsewhere and
 * sees them named on the decision step as not yet judged.
 *
 * **`nutrition` appears only where the week is not already answered** (D-096,
 * C6.4). Where the user answers daily — a coach's plan, a target they set
 * themselves, or an external app whose daily questions they switched on — the
 * week's counts are *derived* from those days and asking again would be asking
 * the same question twice. It is left to the caller to decide, because deciding
 * it here would mean this model reading a nutrition plan.
 */
export function reviewStepsFor(options: {
  readonly bodyCompositionActive: boolean
  readonly strengthActive: boolean
  readonly nutritionSummaryRequired?: boolean
}): readonly WeeklyReviewStep[] {
  return WEEKLY_REVIEW_STEPS.filter((step) => {
    if (step === 'trend' || step === 'measurements') return options.bodyCompositionActive
    if (step === 'strength') return options.bodyCompositionActive || options.strengthActive
    if (step === 'nutrition') return options.nutritionSummaryRequired === true
    return true
  })
}

/**
 * Whether the weekly review has to ask about nutrition at all (D-096).
 *
 * True in exactly one case: an **external nutrition app** whose daily questions
 * the user left off. They are logging in that app, so Project Sola asks for a
 * lightweight weekly summary instead of the three daily taps — and asks nothing
 * where the daily answers already exist, or where there is no plan.
 *
 * Pure and takes the two fields it reads rather than a whole record, so the
 * caller can answer it from a plan, from a draft, or from nothing at all.
 */
export function nutritionSummaryRequired(
  plan: { readonly source: string; readonly dailyChecksEnabled?: boolean } | undefined,
): boolean {
  if (plan === undefined) return false
  return plan.source === 'externalNutritionApp' && plan.dailyChecksEnabled !== true
}

/**
 * Where to reopen a wizard whose step list may have changed under it.
 *
 * A draft can hold a step the current selection no longer includes — the user
 * paused a domain mid-week, which is a thing D-090 explicitly allows. It resumes
 * at the **nearest earlier surviving step**, so answers already given are not
 * discarded and no question is skipped.
 *
 * **One case moves the user forward, and it is unavoidable.** A draft on `trend`
 * whose owner has since paused body composition has no earlier step to fall back
 * to — `trend` is the first in the list. There the only options are the first
 * surviving step or nothing at all, so it takes the first. That is not a
 * compromise of the rule; it is what the rule reduces to when there is nothing
 * behind the user.
 *
 * The same rule as Project Setup, and deliberately written twice rather than
 * shared: the two wizards have different step vocabularies, and a generic helper
 * over `readonly string[]` would lose the type that makes each one exhaustive.
 */
export function resumeStepWithin(
  record: WeeklyReviewRecord,
  available: readonly WeeklyReviewStep[],
): WeeklyReviewStep {
  const first = available[0] ?? WEEKLY_REVIEW_STEPS[0]
  const wanted = resumeStepFor(record)
  if (available.includes(wanted)) return wanted

  const position = WEEKLY_REVIEW_STEPS.indexOf(wanted)
  for (let index = position - 1; index >= 0; index -= 1) {
    const candidate = WEEKLY_REVIEW_STEPS[index]
    if (candidate !== undefined && available.includes(candidate)) return candidate
  }
  return first
}

/** What a draft is created with. Everything else is filled in as the user goes. */
export interface WeeklyReviewDraftInput {
  readonly weekStartDate: string
  readonly phaseId?: PhaseId
  readonly projectId?: ProjectId
}

/**
 * The fields a draft accepts as it is filled in.
 *
 * Deliberately narrower than the record: `status`, `finalizedAt`, `phaseSnapshot`
 * and the envelope are not patchable, so the only route from draft to complete
 * is `finalizeWeeklyReview`.
 */
export interface WeeklyReviewDraftPatch {
  readonly recovery?: RecoveryCheck
  readonly adherence?: AdherenceCheck
  readonly measurementSessionId?: MeasurementSessionId
  readonly notes?: string
  readonly contextTags?: readonly string[]
  readonly phaseId?: PhaseId
  readonly projectId?: ProjectId
  readonly draftStep?: string
}

export function validateWeeklyReviewDraft(input: WeeklyReviewDraftInput): ValidationIssues {
  const issues: ValidationIssue[] = []

  if (toCalendarDate(input.weekStartDate) === undefined) {
    issues.push(issue('weekStartDate', 'Week start must be a real date in YYYY-MM-DD form.'))
  }

  return issues
}

export function validateContextTags(tags: readonly string[]): ValidationIssues {
  const issues: ValidationIssue[] = []
  for (const tag of tags) {
    if (!isContextTag(tag)) {
      issues.push(issue('contextTags', `Unknown context tag: ${tag}.`))
    }
  }
  return issues
}

export function validatePhaseSnapshot(snapshot: PhaseSnapshot): ValidationIssues {
  const issues: ValidationIssue[] = []

  if (!isPhaseType(snapshot.phaseType)) {
    issues.push(issue('phaseSnapshot.phaseType', 'Phase snapshot carries an unknown phase type.'))
  }
  if (!Number.isInteger(snapshot.weekNumber) || snapshot.weekNumber < 1) {
    issues.push(issue('phaseSnapshot.weekNumber', 'Phase week number starts at 1.'))
  }
  if (
    !Number.isFinite(snapshot.targetWeeklyChangeMinPercent) ||
    !Number.isFinite(snapshot.targetWeeklyChangeMaxPercent) ||
    snapshot.targetWeeklyChangeMinPercent > snapshot.targetWeeklyChangeMaxPercent
  ) {
    issues.push(
      issue('phaseSnapshot.targetWeeklyChange', 'Phase snapshot carries an invalid target range.'),
    )
  }

  return issues
}

type ValidationIssue = ValidationIssues[number]

/** Build a new draft. Pure — no clock, no storage. */
export function buildWeeklyReviewDraft(
  input: WeeklyReviewDraftInput,
  now: IsoTimestamp,
): Result<WeeklyReviewRecord, ValidationIssues> {
  const issues = validateWeeklyReviewDraft(input)
  if (issues.length > 0) return err(issues)

  const weekStartDate = toCalendarDate(input.weekStartDate)
  if (weekStartDate === undefined) {
    return err([issue('weeklyReview', 'Weekly review failed construction.')])
  }

  return ok({
    id: newId<'WeeklyReviewId'>(),
    weekStartDate,
    status: 'draft',
    ...(input.phaseId === undefined ? {} : { phaseId: input.phaseId }),
    ...(input.projectId === undefined ? {} : { projectId: input.projectId }),
    contextTags: [],
    createdAt: now,
    updatedAt: now,
    schemaVersion: DB_SCHEMA_VERSION,
  })
}

/**
 * Apply a patch to a draft.
 *
 * Refuses a finalized review rather than quietly producing a new value the
 * caller might store. The repository refuses it too — this is the pure half of
 * the same rule, so a caller that never reaches storage still cannot mutate a
 * finished week.
 */
export function applyWeeklyReviewDraftPatch(
  existing: WeeklyReviewRecord,
  patch: WeeklyReviewDraftPatch,
  now: IsoTimestamp,
): Result<WeeklyReviewRecord, ValidationIssues> {
  if (existing.status === 'complete') {
    return err([
      issue(
        'status',
        'This weekly review is finished and cannot be changed. Corrections to a finished week are not available yet.',
      ),
    ])
  }

  const tagIssues = patch.contextTags === undefined ? [] : validateContextTags(patch.contextTags)
  if (tagIssues.length > 0) return err(tagIssues)

  if (patch.draftStep !== undefined && !isWeeklyReviewStep(patch.draftStep)) {
    return err([issue('draftStep', `Unknown review step: ${patch.draftStep}.`)])
  }

  return ok({
    ...existing,
    ...(patch.draftStep === undefined ? {} : { draftStep: patch.draftStep as WeeklyReviewStep }),
    ...(patch.recovery === undefined ? {} : { recovery: patch.recovery }),
    ...(patch.adherence === undefined ? {} : { adherence: patch.adherence }),
    ...(patch.measurementSessionId === undefined
      ? {}
      : { measurementSessionId: patch.measurementSessionId }),
    ...(patch.notes === undefined ? {} : { notes: patch.notes }),
    ...(patch.contextTags === undefined
      ? {}
      : { contextTags: patch.contextTags as readonly ContextTag[] }),
    ...(patch.phaseId === undefined ? {} : { phaseId: patch.phaseId }),
    ...(patch.projectId === undefined ? {} : { projectId: patch.projectId }),
    updatedAt: now,
  })
}

export interface FinalizeWeeklyReviewInput {
  readonly phaseSnapshot: PhaseSnapshot
  /**
   * Optional, because a body-composition-only build finalized reviews without
   * one and a cardio-only user may have no nutrition plan or commitment to
   * snapshot. **Absent means "not recorded", never "empty plan"** — the two
   * render differently and mean opposite things.
   */
  readonly planSnapshot?: PlanSnapshot
  readonly opinionId: OpinionId
}

/**
 * Freeze a draft.
 *
 * A finalized review is not editable, so this is the last write the record ever
 * receives through the ordinary path. Finalizing twice is refused rather than
 * treated as a no-op: the second call would carry a different snapshot and a
 * different opinion, and silently discarding either is worse than failing.
 */
export function finalizeWeeklyReviewRecord(
  existing: WeeklyReviewRecord,
  input: FinalizeWeeklyReviewInput,
  now: IsoTimestamp,
): Result<WeeklyReviewRecord, ValidationIssues> {
  if (existing.status === 'complete') {
    return err([issue('status', 'This weekly review has already been finished.')])
  }

  const issues = validatePhaseSnapshot(input.phaseSnapshot)
  if (issues.length > 0) return err(issues)

  return ok({
    ...existing,
    status: 'complete',
    phaseSnapshot: input.phaseSnapshot,
    ...(input.planSnapshot === undefined ? {} : { planSnapshot: input.planSnapshot }),
    opinionId: input.opinionId,
    finalizedAt: now,
    updatedAt: now,
  })
}
